import { Controller, useForm } from "react-hook-form";
import * as yup from "yup";
import { ValidationError } from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import {
  InputWithLabel,
  OutlinedButton,
  PrimaryButton,
  Select,
  TextInput,
} from "@normasteaching/norma-ui";
import { X, Save } from "lucide-react";
import { Modal } from "../../UI/Modal/Modal.tsx";
import {
  BusinessType,
  businessTypeLabels,
} from "../../MoneyTransfer/BusinessType.tsx";
import { italianProvinces } from "../../MoneyTransfer/italianProvinces.ts";
import {
  OrderInvoiceInfoDTO,
  StripeOrderDTO,
} from "../../Stripe/StripeOrderDTO.ts";
import { useUpdateInvoiceBillingInfo } from "./useUpdateInvoiceBillingInfo.ts";
import { usePopupController } from "../../HeyLight/usePopupController.ts";
import CodiceFiscale from "codice-fiscale-js";
import { sanitizeNotVisibleUnicodeCharacters } from "../../MoneyTransfer/sanitizeNotVisibleUnicodeCharacters.tsx";

type CustomerInfo = {
  firstName: string;
  lastName: string;
};

type EditInvoiceFormValues = {
  firstName: string;
  lastName: string;
  businessType: BusinessType;
  businessName?: string;
  taxCode: string;
  vat?: string;
  pec?: string;
  sdiCode?: string;
  address: {
    street: string;
    province: string;
    city: string;
    cap: string;
  };
};

function buildDefaultValues(
  invoiceInfo: OrderInvoiceInfoDTO,
  customerInfo: CustomerInfo,
): EditInvoiceFormValues {
  if (!invoiceInfo.requested) {
    return {
      firstName: customerInfo.firstName,
      lastName: customerInfo.lastName,
      businessType: BusinessType.Person,
      taxCode: "",
      address: { street: "", province: "", city: "", cap: "" },
    };
  }
  const base: EditInvoiceFormValues = {
    firstName: customerInfo.firstName,
    lastName: customerInfo.lastName,
    businessType: invoiceInfo.businessType,
    taxCode: invoiceInfo.taxCode,
    address: { ...invoiceInfo.address },
  };
  if (
    invoiceInfo.businessType === BusinessType.Company ||
    invoiceInfo.businessType === BusinessType.IndividualCompany
  ) {
    base.vat = invoiceInfo.vat;
    base.pec = invoiceInfo.pec;
    base.sdiCode = invoiceInfo.sdiCode;
  }
  if (invoiceInfo.businessType === BusinessType.Company) {
    base.businessName = invoiceInfo.businessName;
  }
  return base;
}

function buildInvoiceInfoDTO(
  values: EditInvoiceFormValues,
): OrderInvoiceInfoDTO {
  if (values.businessType === BusinessType.Person) {
    return {
      requested: true,
      businessType: BusinessType.Person,
      taxCode: values.taxCode,
      address: values.address,
    };
  }
  if (values.businessType === BusinessType.IndividualCompany) {
    return {
      requested: true,
      businessType: BusinessType.IndividualCompany,
      taxCode: values.taxCode,
      vat: values.vat!,
      pec: values.pec,
      sdiCode: values.sdiCode,
      address: values.address,
    };
  }
  return {
    requested: true,
    businessType: BusinessType.Company,
    businessName: values.businessName!,
    taxCode: values.taxCode,
    vat: values.vat!,
    pec: values.pec,
    sdiCode: values.sdiCode,
    address: values.address,
  };
}

const schema = yup
  .object({
    firstName: yup.string().required("Inserisci il nome"),
    lastName: yup.string().required("Inserisci il cognome"),
    businessType: yup
      .mixed<BusinessType>()
      .oneOf(Object.values(BusinessType) as BusinessType[])
      .required("Seleziona la tipologia"),
    businessName: yup.string().when("businessType", {
      is: BusinessType.Company,
      then: (s) => s.required("Inserisci la ragione sociale"),
    }),
    vat: yup.string().when("businessType", {
      is: (type: BusinessType) =>
        [BusinessType.Company, BusinessType.IndividualCompany].includes(type),
      then: (s) => s.required("Inserisci la partita IVA"),
    }),
    pec: yup.string().email("PEC non valida"),
    taxCode: yup
      .string()
      .required("Inserisci il codice fiscale")
      .when("businessType", {
        is: (type: BusinessType) =>
          [BusinessType.Person, BusinessType.IndividualCompany].includes(type),
        then: (s) =>
          s.test("is-valid-cf", "Codice fiscale non valido", (value) => {
            if (!value) return false;
            return CodiceFiscale.check(value);
          }),
      }),
    sdiCode: yup.string(),
    address: yup.object({
      street: yup.string().required("Inserisci la via"),
      city: yup.string().required("Inserisci la città"),
      cap: yup.string().required("Inserisci il CAP"),
      province: yup.string().required("Inserisci la provincia"),
    }),
  })
  .test(
    "has-at-least-sdi-or-pec",
    "Inserisci almeno un codice SDI o PEC",
    (value, { createError }) => {
      if (
        ![BusinessType.Company, BusinessType.IndividualCompany].includes(
          value.businessType as BusinessType,
        )
      )
        return true;
      const isValid = !!value.sdiCode || !!value.pec;
      if (!isValid) {
        const error = "Inserisci almeno un codice SDI o PEC";
        return createError({
          message: () => [
            new ValidationError(error, undefined, "sdiCode"),
            new ValidationError(error, undefined, "pec"),
          ],
        });
      }
      return true;
    },
  );

export function EditInvoiceInfoModal({
  orderId,
  invoiceInfo,
  customerInfo,
  onClose,
  onSaved,
}: {
  orderId: string;
  invoiceInfo: OrderInvoiceInfoDTO;
  customerInfo: CustomerInfo;
  onClose: () => void;
  onSaved: (updatedOrder: StripeOrderDTO) => void;
}) {
  const { updateInvoiceBillingInfo, loading } = useUpdateInvoiceBillingInfo();
  const { openPopup } = usePopupController();

  const {
    register,
    control,
    handleSubmit,
    watch,
    trigger,
    formState: { errors },
  } = useForm<EditInvoiceFormValues>({
    resolver: yupResolver(schema),
    defaultValues: buildDefaultValues(invoiceInfo, customerInfo),
    mode: "onChange",
  });

  const businessType = watch("businessType");
  const companyOrIndividual = [
    BusinessType.Company,
    BusinessType.IndividualCompany,
  ];

  const sdiCodeControl = register("sdiCode");
  const pecControl = register("pec");

  async function onSubmit(values: EditInvoiceFormValues) {
    try {
      const updatedOrder = await updateInvoiceBillingInfo(
        orderId,
        buildInvoiceInfoDTO(values),
        { firstName: values.firstName, lastName: values.lastName },
      );
      onSaved(updatedOrder);
    } catch (e) {
      const err = e as Error;
      openPopup({
        title: "Errore",
        description: `Errore durante il salvataggio: ${err?.message ?? "Errore sconosciuto"}`,
        timeoutMs: 5000,
      });
    }
  }

  return (
    <Modal
      isOpen={true}
      onClose={onClose}
      title="Modifica Dati di Fatturazione"
    >
      <div className="space-y-4 max-h-[70vh] overflow-y-auto pr-1">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <InputWithLabel label="Nome">
            <TextInput
              placeholder="Nome"
              {...register("firstName")}
              errorMessage={errors.firstName?.message}
            />
          </InputWithLabel>

          <InputWithLabel label="Cognome">
            <TextInput
              placeholder="Cognome"
              {...register("lastName")}
              errorMessage={errors.lastName?.message}
            />
          </InputWithLabel>

          <InputWithLabel label="Tipologia">
            <Controller
              name="businessType"
              control={control}
              render={({ field }) => (
                <Select
                  placeholder="Seleziona tipologia"
                  value={field.value}
                  onChange={async (v) => {
                    field.onChange(v);
                    await trigger();
                  }}
                  errorMessage={errors.businessType?.message}
                  options={Object.values(BusinessType).map((type) => ({
                    id: type,
                    name: businessTypeLabels[type],
                  }))}
                />
              )}
            />
          </InputWithLabel>

          {businessType === BusinessType.Company && (
            <InputWithLabel label="Ragione Sociale">
              <TextInput
                placeholder="Ragione Sociale"
                {...register("businessName")}
                errorMessage={errors.businessName?.message}
              />
            </InputWithLabel>
          )}

          <InputWithLabel label="Codice Fiscale">
            <Controller
              name="taxCode"
              control={control}
              render={({ field }) => (
                <TextInput
                  placeholder="Codice Fiscale"
                  value={field.value}
                  onChange={(v) =>
                    field.onChange(v.target.value?.toUpperCase())
                  }
                  errorMessage={errors.taxCode?.message}
                />
              )}
            />
          </InputWithLabel>

          {companyOrIndividual.includes(businessType) && (
            <InputWithLabel label="Partita IVA">
              <Controller
                name="vat"
                control={control}
                render={({ field }) => (
                  <TextInput
                    placeholder="Partita IVA"
                    value={field.value}
                    onChange={(v) =>
                      field.onChange(
                        sanitizeNotVisibleUnicodeCharacters(
                          v.target.value ?? "",
                        ).trim(),
                      )
                    }
                    errorMessage={errors.vat?.message}
                  />
                )}
              />
            </InputWithLabel>
          )}

          {companyOrIndividual.includes(businessType) && (
            <InputWithLabel label="Codice SDI">
              <TextInput
                placeholder="Codice SDI"
                {...sdiCodeControl}
                onChange={async (v) => {
                  await sdiCodeControl.onChange(v);
                  await trigger(["pec", "sdiCode"]);
                }}
                errorMessage={errors.sdiCode?.message}
              />
            </InputWithLabel>
          )}

          {companyOrIndividual.includes(businessType) && (
            <InputWithLabel label="PEC">
              <TextInput
                placeholder="PEC"
                {...pecControl}
                onChange={async (v) => {
                  await pecControl.onChange(v);
                  await trigger(["pec", "sdiCode"]);
                }}
                errorMessage={errors.pec?.message}
              />
            </InputWithLabel>
          )}

          <InputWithLabel label="Via">
            <TextInput
              placeholder="Via e numero civico"
              {...register("address.street")}
              errorMessage={errors.address?.street?.message}
            />
          </InputWithLabel>

          <InputWithLabel label="Città">
            <TextInput
              placeholder="Città"
              {...register("address.city")}
              errorMessage={errors.address?.city?.message}
            />
          </InputWithLabel>

          <InputWithLabel label="CAP">
            <TextInput
              placeholder="CAP"
              {...register("address.cap")}
              errorMessage={errors.address?.cap?.message}
            />
          </InputWithLabel>

          <InputWithLabel label="Provincia">
            <Controller
              name="address.province"
              control={control}
              render={({ field }) => (
                <Select
                  searchable
                  placeholder="Seleziona provincia"
                  value={field.value}
                  onChange={field.onChange}
                  errorMessage={errors.address?.province?.message}
                  options={italianProvinces.map((p) => ({
                    name: p.nome,
                    id: p.sigla,
                  }))}
                />
              )}
            />
          </InputWithLabel>
        </div>

        <div className="flex justify-between gap-2 pt-2">
          <OutlinedButton
            iconLeft={<X className="w-5" />}
            onClick={onClose}
            label="Annulla"
            disabled={loading}
          />
          <PrimaryButton
            iconLeft={<Save className="w-5" />}
            onClick={handleSubmit(onSubmit)}
            label="Salva"
            loading={loading}
          />
        </div>
      </div>
    </Modal>
  );
}
